import React from 'react';
import { Box, Checkbox, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Add, ChevronLeft, ChevronRight, MoreHoriz, ViewKanbanOutlined } from '@mui/icons-material';
import { Droppable } from 'react-beautiful-dnd';
import { useTheme } from '@emotion/react';
import KanbanCard from './KanbanCard';
import { laneTint, readableText, statusColor } from '../statusColors';
import { statusIconComponent } from '../statusIcons';
import { columnCheck, isSelected } from '../selection';

const COLLAPSED_WIDTH = 56;
// ClickUp card surface: no border, a hairline + soft drop shadow.
export const CARD_SHADOW = '0 0 1px rgba(0,0,0,0.27), 0 1px 2px rgba(0,0,0,0.055)';

// Solid status pill: 22px tall, 12px/500 uppercase, roomier than ClickUp's 20px.
// An optional status icon sits before the label in the horizontal pill (the collapsed, vertical pill stays text-only).
const StatusPill = ({ color, icon: Icon = null, children, vertical = false }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            backgroundColor: color, color: readableText(color),
            borderRadius: '6px', p: vertical ? '10px 3px' : '3px 10px', minHeight: vertical ? undefined : 22,
            fontSize: '0.75rem', fontWeight: 500, textTransform: 'uppercase',
            lineHeight: '16px', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
            ...(vertical && { writingMode: 'vertical-rl' }),
        }}
    >
        {Icon && !vertical && <Icon sx={{ fontSize: 14, flexShrink: 0 }} />}
        {children}
    </Box>
);

// Stage counts matter to investors, so the count stays a readable grey, not the status colour.
const COUNT_SX = { fontSize: '0.8125rem', fontWeight: 500, color: '#475467', px: '3px' };

// Header icon buttons: 24px, grey icon (legible on light statuses), status tint 15% on hover / 25% while open.
const headerBtnSx = (color) => ({
    width: 24, height: 24, p: 0, borderRadius: '6px', color: '#475467',
    '& svg': { fontSize: 16 },
    '&:hover': { backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)` },
    '&[aria-expanded="true"]': { backgroundColor: `color-mix(in srgb, ${color} 25%, transparent)` },
});

const NEW_STATUS = 'invited';

// Column-wide checkbox states (cards + header select-all); 26px = 18px box + 8px gap.
const CHECK_HIDDEN = { maxWidth: 0, marginRight: 0, opacity: 0 };
const CHECK_SHOWN = { maxWidth: 26, marginRight: '8px', opacity: 1 };
// Header select-all only: indents the status pill to line up with the card checkboxes while shown.
const HEADER_CHECK_HIDDEN = { marginLeft: 0 };
const HEADER_CHECK_SHOWN = { marginLeft: '0.725rem' };

// Shimmer sweep — reuses the global `@keyframes loading` from index.scss (the same
// animation the initial board skeleton uses) so the look stays consistent.
export const shimmer = {
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'loading 1.5s infinite',
};

// Every Typography in KanbanCard is body1, whose theme lineHeight is a fixed 22px.
// Each skeleton text line therefore lives in a 22px line box so the skeleton card's
// height matches the real card exactly (no layout jump when data lands).
const LINE_BOX = '22px';

const TextLine = ({ width, barHeight = 10, mt }) => (
    <Box sx={{ height: LINE_BOX, mt, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ ...shimmer, width, height: barHeight, borderRadius: '4px' }} />
    </Box>
);

// A placeholder shaped EXACTLY like a real KanbanCard (avatar + name/source
// row + email + location + category chip + optional reel button + action chips), shown while a page
// loads / on first load. `withReel` mimics cards that carry a video pitch.
// Sizes mirror KanbanCard: avatar 36, chip 32 (22 line + 8 pad + 2 border),
// reel 40 (24 icon + 8 padding x2).
export const CardSkeleton = ({ withReel = false } = {}) => (
    <Box sx={{ boxShadow: CARD_SHADOW, borderRadius: '10px', backgroundColor: '#fff', p: '0.95rem 1.1rem', mb: '0.5rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            {/* no checkbox placeholder: it is hidden until hover */}
            {/* avatar + identity */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', flex: 1, minWidth: 0 }}>
                <Box sx={{ ...shimmer, width: 36, height: 36, borderRadius: '10px', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* name (left) + source tag (right), same 22px line */}
                    <Box sx={{ height: LINE_BOX, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                        <Box sx={{ ...shimmer, width: '55%', height: 12, borderRadius: '4px' }} />
                        <Box sx={{ ...shimmer, width: 44, height: 10, borderRadius: '4px', flexShrink: 0 }} />
                    </Box>
                    <TextLine width="90%" mt="3px" />{/* email */}
                    <TextLine width="45%" mt="2px" />{/* location */}
                </Box>
            </Box>
        </Box>
        {/* bottom row: category • sector chip (left) + optional round reel button (right) */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', mt: '0.875rem', minHeight: 40 }}>
            <Box sx={{ ...shimmer, width: withReel ? '55%' : '60%', height: 32, borderRadius: '6px' }} />
            {withReel && <Box sx={{ ...shimmer, width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }} />}
        </Box>
        {/* quick-action chip row (24px chips, 4px gap) */}
        <Box sx={{ display: 'flex', gap: '4px', mt: '0.625rem', minHeight: 24 }}>
            {[0, 1, 2].map((i) => <Box key={i} sx={{ ...shimmer, width: 24, height: 24, borderRadius: '6px' }} />)}
        </Box>
    </Box>
);

// One status column: a lane tinted in the status colour, a solid status pill + count, and a droppable card list.
// The next page auto-loads (infinite scroll) as its bottom sentinel scrolls into view.
const KanbanColumn = ({ column, reelStatuses, onLoadMore, isLoadingMore, selection, anySelected = false, onToggleSelect, onModifierSelect, onToggleColumn, collapsed = false, onToggleCollapse, onOpenMenu, isDragging = false, dragInfo = null, onOpenReel, onCardAction, onManageStatus, onInviteStartup }) => {
    const isGroupDrag = (dragInfo?.count || 0) > 1;
    const theme = useTheme();
    const indigo = theme?.palette?.customColors?.indigo600 || '#444CE7';
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    // Invites always land in New, so "+" lives on that column only.
    const canInvite = !!onInviteStartup && column?.value === NEW_STATUS;
    // Same dark menu as the page kebab and card ⋯ menu.
    const menuItemSx = {
        padding: '0.625rem 1rem', gap: '8px', fontSize: '0.875rem',
        color: 'rgba(255, 255, 255, 0.72)',
        '& svg': { fontSize: 18 },
        '&:hover': { backgroundColor: theme?.palette?.customColors?.g500, color: '#fff' },
    };
    const menuDividerSx = { ...menuItemSx, borderBottom: `1px solid ${theme?.palette?.customColors?.g700}` };
    const color = statusColor(column);
    const tint = laneTint(color);
    const dropTint = laneTint(color, 0.14);
    const cards = column?.cards || [];
    const total = column?.count ?? cards.length;
    const hasMore = cards.length < total;
    // Header tick comes from this column's selection state, never from which cards are loaded.
    const check = columnCheck(selection, total);
    const allSelected = check === 'all';
    const someSelected = check === 'some';

    // Auto-load the next page when the bottom sentinel enters the viewport.
    // Refs keep the latest values so the observer is set up once per sentinel mount.
    const sentinelRef = React.useRef(null);
    const intersectingRef = React.useRef(false);
    const stateRef = React.useRef({});
    stateRef.current = { hasMore, isLoadingMore, isDragging, column, onLoadMore };

    const maybeLoad = React.useCallback(() => {
        const s = stateRef.current;
        if (intersectingRef.current && s.hasMore && !s.isLoadingMore && !s.isDragging) {
            s.onLoadMore(s.column);
        }
    }, []);

    React.useEffect(() => {
        if (!hasMore) return undefined;
        const el = sentinelRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        const obs = new IntersectionObserver(
            (entries) => {
                intersectingRef.current = entries[0]?.isIntersecting || false;
                if (intersectingRef.current) maybeLoad();
            },
            { rootMargin: '150px' }
        );
        obs.observe(el);
        return () => obs.disconnect();
        // collapsed: the sentinel unmounts in a strip, so re-attach when the column expands
    }, [hasMore, maybeLoad, collapsed]);

    // After a page finishes loading, if the sentinel is still in view, keep going.
    React.useEffect(() => {
        if (!isLoadingMore) maybeLoad();
    }, [isLoadingMore, maybeLoad]);

    return (
        // Outer shell: spans the full board height but is invisible - no background, no
        // border. Only the inner container below is painted, and it is sized to its cards.
        // The height is here purely so the droppable can extend past the visible container.
        <Box
            sx={{
                width: collapsed ? COLLAPSED_WIDTH : { xs: 320, sm: 384 },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <Droppable droppableId={String(column._id)}>
                {(provided, snapshot) => (
                    // The droppable is the full-height shell, not the card list. Everything
                    // below the visible container is still inside it, so a card released in
                    // that empty space lands in THIS column instead of the last one hovered.
                    <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
                    >
                        {collapsed ? (
                            // Collapsed: a slim strip (count + vertical name). Still a drop target -
                            // a card dropped here lands at the top of this status.
                            <Box
                                role="button"
                                tabIndex={0}
                                aria-label={`Expand ${column?.label} (${total})`}
                                onClick={() => onToggleCollapse?.(column)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleCollapse?.(column); } }}
                                sx={{
                                    backgroundColor: snapshot.isDraggingOver ? dropTint : tint,
                                    borderRadius: '12px',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
                                    py: '1rem', cursor: 'pointer', minHeight: 200,
                                    transition: 'background-color 0.15s ease',
                                    '&:hover': { backgroundColor: dropTint },
                                    '&:focus-visible': { outline: `2px solid ${indigo}`, outlineOffset: '2px' },
                                }}
                            >
                                <Tooltip title="Expand column" placement="right">
                                    <ChevronRight sx={{ fontSize: 20, color: '#475467' }} />
                                </Tooltip>
                                <Typography sx={COUNT_SX}>{total}</Typography>
                                <StatusPill color={color} vertical>{column?.label}</StatusPill>
                                {/* rbd needs the placeholder rendered; zero-height so the strip keeps its width. */}
                                <Box sx={{ height: 0, overflow: 'hidden' }}>{provided.placeholder}</Box>
                            </Box>
                        ) : (
                        <>
                        {/* Visible container - grows with its cards and no further, then
                            caps at the board height and scrolls inside. */}
                        <Box
                            sx={{
                                backgroundColor: snapshot.isDraggingOver ? dropTint : tint,
                                borderRadius: '12px 12px 8px 8px',
                                // Checkboxes show together on column hover; stay on once anything is selected.
                                // Hidden = zero width (no empty gutter); shown = slides in and pushes the content right.
                                '& .kb-check': {
                                    flexShrink: 0, overflow: 'hidden',
                                    transition: 'max-width 0.15s ease, margin-right 0.15s ease, opacity 0.15s ease',
                                    ...(anySelected ? CHECK_SHOWN : CHECK_HIDDEN),
                                },
                                '&:hover .kb-check, & .kb-check:focus-within': CHECK_SHOWN,
                                '& .kb-header-check': { transition: 'margin-left 0.15s ease', ...(anySelected ? HEADER_CHECK_SHOWN : HEADER_CHECK_HIDDEN) },
                                '&:hover .kb-header-check, & .kb-header-check:focus-within': HEADER_CHECK_SHOWN,
                                '@media (hover: none)': { '& .kb-check': CHECK_SHOWN, '& .kb-header-check': HEADER_CHECK_SHOWN },
                                // Header actions show on hover anywhere in the column, cards included, not just over the header.
                                '&:hover .kb-header-actions, & .kb-header-actions:focus-within': { opacity: 1 },
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: '100%',
                                minHeight: 0,
                                overflow: 'hidden',
                                transition: 'background-color 0.15s ease',
                            }}
                        >
                            {/* Header - ClickUp layout: pill + count, then collapse / options / add on hover. */}
                            <Box
                                sx={{
                                    // Left padding keeps the checkbox in line with the card checkboxes.
                                    p: '8px 8px 8px 0.875rem', height: 40,
                                    flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                                    {/* Bulk triage is a daily job here, so select-all sits by the pill, not in the menu like ClickUp. */}
                                    {/* No select-all for an empty column: there is no startup to select. */}
                                    {onToggleColumn && total > 0 && (
                                        <Tooltip title={allSelected ? 'Deselect all' : `Select all in ${column?.label}`}>
                                            <Box component="span" className="kb-check kb-header-check" sx={{ display: 'flex' }}>
                                                <Checkbox
                                                    size="small"
                                                    checked={allSelected}
                                                    indeterminate={someSelected}
                                                    disabled={total === 0 || isLoadingMore || isDragging}
                                                    onChange={() => onToggleColumn(column)}
                                                    inputProps={{ 'aria-label': `Select all startups in ${column?.label}` }}
                                                    sx={{
                                                        p: 0,
                                                        color: '#D0D5DD',
                                                        '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: indigo },
                                                        '& .MuiSvgIcon-root': { fontSize: 18 },
                                                    }}
                                                />
                                            </Box>
                                        </Tooltip>
                                    )}
                                    <StatusPill color={color} icon={statusIconComponent(column)}>{column?.label}</StatusPill>
                                    <Typography sx={COUNT_SX}>{total}</Typography>
                                </Box>
                                {/* Hidden until hover/focus; always shown on touch screens and while the menu is open. */}
                                <Box
                                    className="kb-header-actions"
                                    sx={{
                                        display: 'flex', alignItems: 'center', flexShrink: 0,
                                        opacity: menuAnchor ? 1 : 0, transition: 'opacity 0.15s ease',
                                        '@media (hover: none)': { opacity: 1 },
                                    }}
                                >
                                    {onToggleCollapse && (
                                        <Tooltip title="Collapse column">
                                            <IconButton aria-label={`Collapse ${column?.label}`} onClick={() => onToggleCollapse(column)} sx={headerBtnSx(color)}>
                                                <ChevronLeft />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Column options">
                                        <IconButton
                                            aria-label={`${column?.label} options`}
                                            aria-haspopup="menu"
                                            aria-expanded={menuAnchor ? 'true' : 'false'}
                                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                                            sx={headerBtnSx(color)}
                                        >
                                            <MoreHoriz />
                                        </IconButton>
                                    </Tooltip>
                                    {canInvite && (
                                        <Tooltip title="Invite Startup">
                                            <IconButton aria-label="Invite Startup" onClick={onInviteStartup} sx={headerBtnSx(color)}>
                                                <Add />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Menu
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={() => setMenuAnchor(null)}
                                    onClick={() => setMenuAnchor(null)}
                                    PaperProps={{
                                        elevation: 0,
                                        sx: {
                                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                            mt: 1, padding: '8px', borderRadius: '8px',
                                            '& .MuiList-root': { padding: '0 !important' },
                                            bgcolor: theme?.palette?.customColors?.g400,
                                            color: theme?.palette?.customColors?.whiteBG,
                                            minWidth: '200px',
                                        },
                                    }}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                >
                                    {onToggleCollapse && (
                                        <MenuItem disableRipple sx={onManageStatus ? menuDividerSx : menuItemSx} onClick={() => onToggleCollapse(column)}>
                                            <ChevronLeft /> Collapse column
                                        </MenuItem>
                                    )}
                                    {onManageStatus && (
                                        <MenuItem disableRipple sx={menuItemSx} onClick={onManageStatus}>
                                            <ViewKanbanOutlined /> Manage Status
                                        </MenuItem>
                                    )}
                                </Menu>
                            </Box>

                            {/* Card list - the column's own scroller once it fills up. */}
                            <Box
                                sx={{
                                    // Top padding keeps the selected card's ring clear of the scroll edge.
                                    p: '3px 0.5rem 0.5rem',
                                    minHeight: 0,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    // Scrollbar is always visible once the list scrolls.
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#9e9e9e transparent',
                                    '&::-webkit-scrollbar': { width: '0.313rem' },
                                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#9e9e9e', borderRadius: '6px' },
                                    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                                }}
                            >
                                {/* Group drag: the other selected cards leave the column until the drop. */}
                                {(isGroupDrag
                                    ? cards.filter((c) => !isSelected(selection, c.startup_id) || String(c.pipeline_id) === String(dragInfo.pipelineId))
                                    : cards
                                ).map((card, i) => {
                                    const isLifted = isGroupDrag && String(dragInfo.pipelineId) === String(card.pipeline_id);
                                    return (
                                        <KanbanCard
                                            key={card.pipeline_id}
                                            card={card}
                                            index={i}
                                            reelStatuses={reelStatuses}
                                            selected={isSelected(selection, card.startup_id)}
                                            onToggleSelect={onToggleSelect}
                                            onModifierSelect={onModifierSelect}
                                            dragCount={isLifted ? dragInfo.count : 0}
                                            onOpenReel={onOpenReel}
                                            onOpenMenu={onOpenMenu}
                                            onCardAction={onCardAction}
                                        />
                                    );
                                })}
                                {provided.placeholder}

                                {cards.length === 0 && !snapshot.isDraggingOver && (
                                    <Typography sx={{ textAlign: 'center', color: '#98A2B3', fontSize: '0.8rem', py: '2rem' }}>
                                        Drag and drop here
                                    </Typography>
                                )}

                                {/* While the next page loads, show card-shaped skeletons (ClickUp feel). */}
                                {/* Next page's reel flags are unknown, so don't guess — no reel placeholder. */}
                                {isLoadingMore &&
                                    Array.from({ length: Math.min(2, Math.max(1, total - cards.length)) }).map((_, i) => (
                                        <CardSkeleton key={`sk-${i}`} />
                                    ))}

                                {/* Invisible sentinel — when it scrolls into view the next page auto-loads. */}
                                {hasMore && <Box ref={sentinelRef} sx={{ height: '1px' }} />}
                            </Box>
                        </Box>
                        </>
                        )}
                    </Box>
                )}
            </Droppable>
        </Box>
    );
};

// `selection` is this column's own entry, so other columns' changes keep it identical.
export default React.memo(KanbanColumn);
